<template>
  <InputField
    v-bind="$props"
    :invalid-feedback="invalidFeedback ?? errorMessage"
    :value="value"
    @input="value = Number((<HTMLInputElement>$event.target).value)"
  >
    <template v-if="$slots.label" #label>
      <slot name="label"></slot>
    </template>
    <template v-if="$slots.prefix" #prefix>
      <slot name="prefix"></slot>
    </template>
  </InputField>
</template>

<script lang="ts">
export type NumberFieldProps = InputFieldProps & {
  max?: number;
  modelValue?: number;
  min?: number;
  name?: string;
  required?: boolean;
  rules?: NumberSchema;
  type?: string;
};

export type NumberFieldEvents = {
  (event: "update:modelValue", value: number): unknown;
};
</script>

<script lang="ts" setup>
import { uniqueId } from "underscore";
import { RuleExpression, useField } from "vee-validate";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { NumberSchema, number } from "yup";
import InputField, { InputFieldProps } from "./InputField.vue";

const props = withDefaults(defineProps<NumberFieldProps>(), {
  id: uniqueId("field-"),
  max: Infinity,
  min: -Infinity,
  type: "number",
});
defineEmits<NumberFieldEvents>();

const { t } = useI18n();

const rules = computed<RuleExpression<number | undefined>>(() => {
  let schema = number();

  if (props.rules) {
    schema = schema.concat(props.rules);
  }
  if (props.required) {
    schema = schema.required(t("form.field.number.invalid.required"));
  }
  schema = schema.max(
    props.max,
    t("form.field.number.invalid.max", { n: props.max })
  );
  schema = schema.min(
    props.min,
    t("form.field.number.invalid.min", { n: props.min })
  );

  return schema;
});

const { value, errorMessage } = useField<number | undefined>(
  () => props.name ?? props.id,
  rules,
  {
    syncVModel: true,
  }
);
</script>
